<template>
  <app-page
    :title="$t('employee.enrollLoading.pageTitle')"
    :error="error"
    :previous="[
      { label: $t('employee.previousLabel'), to: { name: 'employees' } },
      {
        label: employee ? employee.full_name : $t('employee.title'),
        to: { name: 'employee', params: { employeeId: $route.params.employeeId } },
      },
    ]"
  >
    <template v-if="loading">
      <sme-alert level="info" class="mb-3">{{ $t('employee.enrollLoading.waitBody') }}</sme-alert>
      <app-loading :loading="true" />
    </template>
    <template v-else>
      <template v-if="success">
        <sme-alert level="info" class="mb-3">
          <template v-if="!isSelfFunded()">{{ $t('employee.enrollLoading.notSelfFundedBody') }}</template>
          <template v-else>
            <p>
              {{ $t('employee.enrollLoading.selfFundedBody1') }}
            </p>
            <p>
              {{ $t('employee.enrollLoading.selfFundedBody2') }}
            </p>
            <p>
              {{ $t('employee.enrollLoading.selfFundedBody3') }}
            </p>
          </template>
        </sme-alert>

        <div class="mb-3">
          <app-input
            v-if="!isSelfFunded()"
            v-model="employee.full_name"
            type="text"
            name="full name"
            :label="$t('employee.enrollLoading.employeeNameLabel')"
            readonly
          ></app-input>
          <app-input
            v-else
            v-model="employee.work_email"
            type="text"
            name="full name"
            :label="$t('admins.add.emailLabel')"
            readonly
          ></app-input>
          <app-input
            v-if="!isSelfFunded()"
            v-model="employee.employee_code"
            type="text"
            name="employee code"
            :label="$t('employee.employeeCodeLabel')"
            readonly
          ></app-input>

          <app-input
            v-model="newSortCode"
            type="text"
            name="sort code"
            :label="$t('employee.enrollLoading.newBankingFieldLabel', { label: getBankingInfo.sortCode.label })"
            readonly
          ></app-input>
          <!-- add copy -->

          <app-input
            v-model="newAccountNumber"
            type="text"
            name="account number"
            :label="$t('employee.enrollLoading.newBankingFieldLabel', { label: getBankingInfo.accountNumber.label })"
            readonly
          ></app-input>
          <!-- add copy -->
        </div>

        <ValidationObserver v-slot="v" v-if="!isSelfFunded()">
          <b-form @submit.prevent="v.handleSubmit(onSubmit)" id="updated">
            <ValidationProvider name="updated" :rules="{ required: { allowFalse: false } }" v-slot="f">
              <b-form-checkbox v-model="updated">{{
                $t('employee.enrollLoading.confirmUpdatedCheckboxLabel')
              }}</b-form-checkbox>
              <b-form-invalid-feedback v-for="fError in f.errors" :key="fError">{{ fError }}</b-form-invalid-feedback>
            </ValidationProvider>
            <page-sub-footer>
              <b-button type="submit" variant="primary" :disabled="v.invalid || saving">
                <b-spinner v-if="saving" class="mr-2" small />
                {{ saving ? $t('employee.enrollLoading.submittingButton') : $t('employee.enrollLoading.submitButton') }}
              </b-button>
            </page-sub-footer>
          </b-form>
        </ValidationObserver>
        <page-sub-footer v-else>
          <router-link
            v-if="!isManagingSelf()"
            :to="{ name: 'employee', params: { employeeId: employee.employee_id } }"
          >
            <b-button type="submit" variant="primary">{{ $t('employee.enrollLoading.viewEmployeeButton') }}</b-button>
          </router-link>
          <router-link v-else :to="{ name: 'employee-setup', params: { employeeId: employee.employee_id } }">
            <b-button type="submit" variant="primary">{{ $t('employee.enrollLoading.continueSetupButton') }}</b-button>
          </router-link>
        </page-sub-footer>
      </template>

      <sme-alert v-if="!success" level="warning">
        {{ $t('employee.enrollLoading.slowConnectionAlert') }}
      </sme-alert>
    </template>
  </app-page>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import State from '@/state/State';

export default {
  name: 'EnrollEmployeeLoading',
  components: { ValidationObserver, ValidationProvider, AppInput, AppLoading, AppPage, PageSubFooter, SmeAlert },
  data() {
    return {
      error: undefined,
      loading: true,
      saving: false,
      updated: false,
      employee: undefined,
      state: State.state,

      success: false,

      newSortCode: undefined,
      newAccountNumber: undefined,
    };
  },
  methods: {
    retriveEmployeeNewState(retries) {
      // Try to get the newly set sort and banking codes in SetupEmployee
      // It might take a while, so retry 3 times or show an error
      ApiClient.getEmployee(this.$route.params.employeeId).then(
        employee => {
          if (employee.current_state !== 'ENROLLING' && employee.current_state !== 'ENROLLED') {
            this.$router.push({
              name: 'employee',
              params: { employeeId: employee.employee_id },
            });
          }

          this.employee = employee;
          if (employee.properties.payment_sort_code && employee.properties.payment_account_number) {
            this.newSortCode = employee.properties.payment_sort_code;
            this.newAccountNumber = employee.properties.payment_account_number;
            this.success = true;
            this.loading = false;
          } else {
            if (retries > 0) {
              setTimeout(() => this.retriveEmployeeNewState(retries - 1), 5000);
            } else {
              this.loading = false;
              this.success = false;
            }
          }
        },
        error => {
          this.error = error.message;
          this.loading = false;
          this.success = false;
        },
      );
    },
    onSubmit() {
      this.saving = true;
      const newEmployee = {
        ...this.employee,
        properties: {
          ...this.employee.properties,
          updated_payroll: true,
        },
      };
      ApiClient.updateEmployee(newEmployee).then(
        () => {
          this.$router.push({
            name: 'employee',
            params: {
              employeeId: this.employee.employee_id,
            },
          });
        },
        error => {
          this.error = error.message;
          this.loading = false;
          this.saving = false;
        },
      );
    },
    loadData() {
      this.loading = true;
      this.retriveEmployeeNewState(12);
    },
    isSelfFunded() {
      return State.state?.isSelfServeSME;
    },
    isManagingSelf() {
      return State.state?.user?.email == this.employee.work_email;
    },
  },
  mounted() {
    this.loadData();
  },
  computed: {
    getBankingInfo: () => getBankingInfo(),
  },
};
</script>

<style scoped>
#updated .invalid-feedback {
  display: block;
}
</style>
